import React, { useState, useEffect } from "react";
import { Button, Divider, Input, Upload } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const TermsConditionsEditor = ({
  slide,
  updateSlide,
  removeSlide,
  triggerSave,
}) => {
  const [allTermsGroup, setAllTermsGroup] = useState(
    slide.allTermsGroup || [{ groupName: "Terms Section 01", detail: [] }]
  );
  const [background, setBackground] = useState(slide.bg || "");
  const [heading, setHeading] = useState(slide.heading || "Terms & Conditions");
  const [tagline, setTagline] = useState(slide.tagline || "");

  useEffect(() => {
    setAllTermsGroup(
      slide.allTermsGroup || [{ groupName: "Terms Section 01", detail: [] }]
    );
    setBackground(slide.bg || "");
    setHeading(slide.heading || "Terms & Conditions");
    setTagline(slide.tagline || "");
  }, [slide]);

  useEffect(() => {
    if (triggerSave) {
      updateSlide(slide.id, {
        allTermsGroup,
        heading,
        tagline,
        bg: background,
      });
    }
  }, [triggerSave]);

  const handleBackgroundChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file && file instanceof Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        setBackground(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addTermsGroup = () => {
    const newGroup = {
      groupName: `Terms Section ${allTermsGroup.length + 1}`,
      detail: [],
    };
    setAllTermsGroup([...allTermsGroup, newGroup]);
  };

  const removeGroup = (groupIndex) => {
    setAllTermsGroup(allTermsGroup.filter((_, i) => i !== groupIndex));
  };

  const addDetail = (groupIndex, type = "object") => {
    setAllTermsGroup((prev) =>
      prev.map((group, idx) =>
        idx === groupIndex
          ? {
              ...group,
              detail: [
                ...group.detail,
                type === "string" ? "" : { heading: "", description: "" },
              ],
            }
          : group
      )
    );
  };

  const updateStringDetail = (groupIndex, detailIndex, value) => {
    setAllTermsGroup((prev) =>
      prev.map((group, gIdx) =>
        gIdx === groupIndex
          ? {
              ...group,
              detail: group.detail.map((item, dIdx) =>
                dIdx === detailIndex ? value : item
              ),
            }
          : group
      )
    );
  };

  const updateObjectDetail = (groupIndex, detailIndex, field, value) => {
    setAllTermsGroup((prev) =>
      prev.map((group, gIdx) =>
        gIdx === groupIndex
          ? {
              ...group,
              detail: group.detail.map((item, dIdx) =>
                dIdx === detailIndex ? { ...item, [field]: value } : item
              ),
            }
          : group
      )
    );
  };

  const removeDetail = (groupIndex, detailIndex) => {
    setAllTermsGroup((prev) =>
      prev.map((group, gIdx) =>
        gIdx === groupIndex
          ? {
              ...group,
              detail: group.detail.filter((_, dIdx) => dIdx !== detailIndex),
            }
          : group
      )
    );
  };

  return (
    <div className="border p-3 rounded bg-white shadow-sm">
      <Divider />
      <h1 className="font-semibold mb-2 text-lg">
        Edit Terms & Conditions Slide
      </h1>

      {/* Background */}
      <div className="mb-2">
        <Upload
          accept="image/*"
          beforeUpload={() => false}
          showUploadList={false}
          onChange={handleBackgroundChange}
        >
          <Button type="primary" icon={<UploadOutlined />}>
            Upload Background
          </Button>
        </Upload>
        {background && (
          <img
            src={background}
            alt="Background Preview"
            className="block mt-2 w-full h-32 max-w-xs max-h-xs object-cover rounded-lg border"
          />
        )}
      </div>

      {/* Heading */}
      <div className="mb-2">
        <label className="font-medium">Slide Heading</label>
        <Input value={heading} onChange={(e) => setHeading(e.target.value)} />
      </div>

      <div className="mb-2">
        <label className="font-medium">Slide Tagline</label>
        <Input value={tagline} onChange={(e) => setTagline(e.target.value)} />
      </div>

      {allTermsGroup.map((group, gIdx) => (
        <div key={gIdx} className="border p-3 mb-2 rounded">
          <Input
            className="mb-2"
            value={group.groupName}
            placeholder="Group Name"
            onChange={(e) => {
              const newGroups = [...allTermsGroup];
              newGroups[gIdx].groupName = e.target.value;
              setAllTermsGroup(newGroups);
            }}
          />

          {group.detail.map((item, dIdx) => (
            <div
              key={dIdx}
              className="bg-gray-50 p-2 rounded mb-3 flex flex-col gap-2"
            >
              {typeof item === "string" ? (
                <Input.TextArea
                  className="mb-2"
                  rows={2}
                  value={item}
                  placeholder="Term text"
                  onChange={(e) =>
                    updateStringDetail(gIdx, dIdx, e.target.value)
                  }
                />
              ) : (
                <>
                  <Input
                    placeholder="Heading"
                    className="mb-2"
                    value={item.heading}
                    onChange={(e) =>
                      updateObjectDetail(gIdx, dIdx, "heading", e.target.value)
                    }
                  />
                  <Input.TextArea
                    rows={3}
                    className="mb-2"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) =>
                      updateObjectDetail(
                        gIdx,
                        dIdx,
                        "description",
                        e.target.value
                      )
                    }
                  />
                </>
              )}
              <Button
                danger
                icon={<DeleteOutlined />}
                className=""
                onClick={() => removeDetail(gIdx, dIdx)}
              >
                Remove Detail
              </Button>
            </div>
          ))}

          <div className="flex gap-2 mb-2">
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => addDetail(gIdx, "string")}
            >
              Add Text Term
            </Button>
          </div>
        </div>
      ))}

      <div className="mt-6 text-right">
        <Button
          danger
          type="primary"
          icon={<DeleteOutlined />}
          onClick={() => removeSlide(slide.id)}
        >
          Remove Slide
        </Button>
      </div>
    </div>
  );
};

export default TermsConditionsEditor;
